/**
 * Shared document preview loading logic.
 * Used by both the document list table and the chatbot document links.
 */
import { supabase } from "@/integrations/supabase/client";
import { isIfcFile } from "@/domains/documents/constants/fileTypes";
import { fetchDwgChildPdfForPreview } from "./queries";
import type { Document } from "@/types/documents";

export interface PreviewResult {
  document: Document;
  /** Pre-loaded blob URL, or null for IFC files (which open the 3D viewer
   *  modal instead of rendering the blob — see loadDocumentPreview). */
  blobUrl: string | null;
  /** Raw blob data for Office file rendering, or null for IFC files. */
  blobData: Blob | null;
  /** Original DWG file_path (for download button), null for non-DWG files */
  originalDwgFilePath: string | null;
}

interface DocumentLike {
  id: string | number;
  filename: string;
  file_path: string;
  building_id: string | number;
  content_type: string | null;
  size: number | null;
  created_at: string;
  parent_document_id?: string | number | null;
  version?: number;
  created_by_name?: string;
}

/**
 * Load a document for preview. Handles DWG→child PDF resolution,
 * blob download, and constructs the Document object for DocumentViewerDialog.
 *
 * This is the single source of truth for preview loading logic —
 * both the document list and the chat sidebar use this.
 */
export async function loadDocumentPreview(doc: DocumentLike): Promise<PreviewResult> {
  let filePathToPreview = doc.file_path;
  let contentTypeForPreview = doc.content_type;
  let originalDwgFilePath: string | null = null;

  // WHA-3604: an .ifc preview only opens a small "open in 3D viewer?" modal,
  // so there's nothing to render here — skip the (potentially hundreds-of-MB)
  // blob download and return metadata only so the dialog opens instantly. The
  // DocumentViewerDialog IFC branch ignores blobUrl/blobData entirely.
  if (isIfcFile(doc.filename)) {
    return {
      document: {
        id: String(doc.id),
        filename: doc.filename,
        file_path: doc.file_path,
        building_id: String(doc.building_id),
        content_type: doc.content_type || "application/octet-stream",
        size: doc.size ?? 0,
        created_at: doc.created_at,
        parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
        version: doc.version ?? undefined,
        created_by_name: doc.created_by_name ?? undefined,
      },
      blobUrl: null,
      blobData: null,
      originalDwgFilePath: null,
    };
  }

  // For DWG files, fetch the child PDF to preview instead
  const isDwgFile = doc.filename?.toLowerCase().endsWith(".dwg");
  if (isDwgFile) {
    const childPdf = await fetchDwgChildPdfForPreview(String(doc.id));
    if (!childPdf) {
      throw new Error("DWG file conversion not complete. Child PDF not found.");
    }
    originalDwgFilePath = doc.file_path;
    filePathToPreview = childPdf.file_path;
    contentTypeForPreview = childPdf.content_type || "application/pdf";
  }

  // Download the blob from Supabase storage
  const { data, error } = await supabase.storage.from("documents").download(filePathToPreview);

  if (error) {
    // Parse Supabase error
    let errMsg = "File not available";
    if (typeof error === "string") {
      errMsg = error;
    } else if (typeof error === "object" && error !== null) {
      const errObj = error as Record<string, unknown>;
      if (typeof errObj.message === "string" && errObj.message) {
        errMsg = errObj.message;
      }
    }
    throw new Error(errMsg);
  }

  // Create blob with correct content type
  const blob = new Blob([data], {
    type: contentTypeForPreview || "application/octet-stream",
  });
  const blobUrl = URL.createObjectURL(blob);

  // Build Document object for DocumentViewerDialog
  // Shows original document info but previews the child PDF for DWG files
  const documentForPreview: Document = {
    id: String(doc.id),
    filename: doc.filename,
    file_path: filePathToPreview,
    building_id: String(doc.building_id),
    content_type: contentTypeForPreview || "application/octet-stream",
    size: doc.size ?? 0,
    created_at: doc.created_at,
    parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
    version: doc.version ?? undefined,
    created_by_name: doc.created_by_name ?? undefined,
  };

  return {
    document: documentForPreview,
    blobUrl,
    blobData: blob,
    originalDwgFilePath,
  };
}
